<template>
    <div class="supplier-management">
        <!-- 헤더 -->
        <div class="content-header">
            <h3>
                <i class="fas fa-building"></i>
                공급자 관리
            </h3>
            <div class="header-content-group">
                <!-- 검색 바 -->
                <div class="search-bar-header">
                    <div class="form-field-inline">
                        <select v-model="filterEnergySource" class="form-select-header" @change="handleSearch">
                            <option value="">에너지원 전체</option>
                            <option v-for="energy in uniqueEnergySources" :key="energy.code" :value="energy.code">
                                {{ energy.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="header-actions-group">
                    <!-- <button class="btn-action btn-excel" @click="exportToExcel">
                    <i class="fas fa-file-excel"></i>
                    Excel
                </button> -->
                    <button class="btn-action btn-primary" @click="openCreateForm">
                        <i class="fas fa-plus"></i>
                        신규 등록
                    </button>
                </div>
            </div>
        </div>

        <!-- 공급자 목록 테이블 -->
        <div class="data-table-container">
            <table class="settings-data-table">
                <thead>
                    <tr>
                        <th>공급자 코드</th>
                        <th>공급자명</th>
                        <th>에너지원</th>
                        <th class="text-center">상태</th>
                        <th class="text-center">작업</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="supplier in filteredSuppliers"
                        :key="supplier.supplierIdx"
                        class="clickable-row"
                        @click="openViewForm(supplier)"
                    >
                        <td>
                            <span>{{ supplier.supplierCode }}</span>
                        </td>
                        <td>
                            <span>{{ supplier.supplierName }}</span>
                        </td>
                        <td>
                            <span>{{ getEnergySourceName(supplier.energySourceCode) }}</span>
                        </td>
                        <td class="text-center">
                            <span :class="['status-badge', supplier.enabled === 'Y' ? 'active' : 'inactive']">
                                {{ supplier.enabled === "Y" ? "활성" : "비활성" }}
                            </span>
                        </td>
                        <td class="text-center" @click.stop>
                            <button class="btn-edit" @click="openEditForm(supplier)">
                                <i class="fas fa-edit"></i>
                                수정
                            </button>
                            <button class="btn-delete" @click="handleDelete(supplier)">
                                <i class="fas fa-trash-alt"></i>
                                삭제
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 공급자 등록/수정/상세 폼 (슬라이드 패널) -->
        <SettingsSidePanel :show="showForm" :title="formTitle" size="default" @close="closeForm">
            <div class="form-group">
                <label> 공급자 코드 <span v-if="!isViewMode" class="required">*</span> </label>
                <input
                    type="text"
                    v-model="form.supplierCode"
                    placeholder="영문 대문자, 10자 이내"
                    maxlength="10"
                    class="form-input"
                    :disabled="isEditMode || isViewMode"
                />
                <small v-if="!isViewMode" class="help-text">영문 대문자, 10자 이내</small>
            </div>

            <div class="form-group">
                <label> 공급자명 <span v-if="!isViewMode" class="required">*</span> </label>
                <input
                    type="text"
                    v-model="form.supplierName"
                    placeholder="공급자명을 입력하세요"
                    class="form-input"
                    :disabled="isViewMode"
                />
            </div>

            <div class="form-group">
                <label> 에너지원 <span v-if="!isViewMode" class="required">*</span> </label>
                <select v-model="form.energySourceCode" class="form-select" :disabled="isViewMode">
                    <option value="">선택하세요</option>
                    <option v-for="energy in energySourceCode" :key="energy.value" :value="energy.value">
                        {{ energy.text }}
                    </option>
                </select>
            </div>

            <div class="form-group">
                <label>상태</label>
                <div class="radio-group">
                    <label class="radio-label">
                        <input type="radio" v-model="form.enabled" value="Y" :disabled="isViewMode" />
                        <span style="margin-left: 5px">활성</span>
                    </label>
                    <label class="radio-label">
                        <input type="radio" v-model="form.enabled" value="N" :disabled="isViewMode" />
                        <span style="margin-left: 5px">비활성</span>
                    </label>
                </div>
            </div>

            <div class="form-group">
                <label>비고</label>
                <textarea
                    v-model="form.remarks"
                    placeholder="텍스트 입력"
                    rows="3"
                    class="form-textarea"
                    :disabled="isViewMode"
                ></textarea>
            </div>

            <!-- 상세 정보 (상세보기 모드일 때만 표시) -->
            <div v-if="isViewMode" class="detail-info">
                <div class="info-row">
                    <span class="info-label">등록일시:</span>
                    <span class="info-value">{{ form.regDate || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">등록자:</span>
                    <span class="info-value">{{ form.regWorker || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">수정일시:</span>
                    <span class="info-value">{{ form.modDate || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">수정자:</span>
                    <span class="info-value">{{ form.modWorker || "-" }}</span>
                </div>
            </div>
            <template #footer>
                <button class="btn-cancel" @click="closeForm">
                    <i class="fas fa-times"></i>
                    {{ isViewMode ? "닫기" : "취소" }}
                </button>
                <button v-if="!isViewMode" class="btn-save" @click="handleSave">
                    <i class="fas fa-save"></i>
                    저장
                </button>
            </template>
        </SettingsSidePanel>
    </div>
</template>

<script>
import SettingsSidePanel from "../settings/SettingsSidePanel.vue";
import backEndApi from "@src/api/backEndApi.js";
import * as XLSX from "xlsx";
import { mapState } from "vuex";

export default {
    name: "SupplierManagement",
    components: {
        SettingsSidePanel,
    },
    data() {
        return {
            searchKeyword: "",
            filterEnergySource: "",
            filterEnabled: "",
            showForm: false,
            isViewMode: false,
            isEditMode: false,
            editingSupplier: null,
            form: {
                supplierCode: null,
                supplierName: null,
                energySourceCode: null,
                enabled: "Y",
                remarks: null,
            },
            // API에서 로드된 데이터
            suppliers: [],
            supplierDetail: null,
        };
    },
    computed: {
        ...mapState({
            energySourceCode: (state) => state.commonCodes.energySourceCode,
        }),
        filteredSuppliers() {
            let filtered = [...this.suppliers];

            // 에너지원 필터링
            if (this.filterEnergySource) {
                filtered = filtered.filter((s) => s.energySourceCode === this.filterEnergySource);
            }

            // 활성화/비활성화 필터링
            if (this.filterEnabled) {
                filtered = filtered.filter((s) => s.enabled === this.filterEnabled);
            }

            // 검색어 필터링
            if (this.searchKeyword.trim()) {
                const searchLower = this.searchKeyword.toLowerCase();
                filtered = filtered.filter(
                    (s) =>
                        s.supplierCode.toLowerCase().includes(searchLower) ||
                        s.supplierName.toLowerCase().includes(searchLower),
                );
            }

            return filtered;
        },
        uniqueEnergySources() {
            // suppliers에서 사용된 에너지원 코드 수집
            const uniqueCodes = new Set();
            this.suppliers.forEach((supplier) => {
                if (supplier.energySourceCode) {
                    uniqueCodes.add(supplier.energySourceCode);
                }
            });
            // energySourceCode 배열을 Map으로 변환 (value -> text 매핑)
            const energySourceMap = new Map();
            if (Array.isArray(this.energySourceCode)) {
                this.energySourceCode.forEach((item) => {
                    if (item.value && item.text) {
                        energySourceMap.set(item.value, item.text);
                    }
                });
            }

            // 사용된 코드들을 배열로 변환하고 정렬
            return Array.from(uniqueCodes)
                .filter((code) => energySourceMap.has(code)) // energySourceCode에 정의된 것만 필터링
                .sort()
                .map((code) => ({
                    code: code,
                    name: energySourceMap.get(code) || code,
                }));
        },
        formTitle() {
            if (this.isViewMode) {
                return "공급자 상세";
            } else if (this.isEditMode) {
                return "공급자 수정";
            } else {
                return "공급자 등록";
            }
        },
    },
    mounted() {
        this.loadData();
    },
    methods: {
        async loadData() {
            try {
                let params = {
                    energySourceCode: "",
                    enabled: "",
                };
                const result = await backEndApi.energyBillCalc.getSupplierList(params);
                if (result.status == 200) {
                    this.suppliers = result.data;
                } else {
                    this.$notify({
                        type: "warning",
                        title: "경고",
                        text: result.data.message,
                    });
                }
            } catch (error) {
                console.error("공급자 목록 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "공급자 목록을 불러오는데 실패했습니다.",
                });
            }
        },
        async loadDetailData(supplierIdx) {
            try {
                const result = await backEndApi.energyBillCalc.getSupplier(supplierIdx);
                if (result.status == 200) {
                    this.supplierDetail = result.data;
                } else {
                    this.$notify({
                        type: "warning",
                        title: "경고",
                        text: result.data.message,
                    });
                }
            } catch (error) {
                console.error("공급자 상세 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "공급자 상세 조회 실패:",
                    error,
                });
            }
        },
        getEnergySourceName(code) {
            if (!code || !Array.isArray(this.energySourceCode)) {
                return code || "-";
            }

            const energySource = this.energySourceCode.find((item) => item.value === code);
            return energySource ? energySource.text : code;
        },
        handleSearch() {
            // 검색은 computed에서 자동 처리됨
        },
        resetSearch() {
            this.searchKeyword = "";
            this.filterEnergySource = "";
            this.filterEnabled = "";
        },
        exportToExcel() {
            try {
                // 엑셀로 내보낼 데이터 준비
                const excelData = this.filteredSuppliers.map((supplier, index) => ({
                    순번: index + 1,
                    공급자코드: supplier.supplierCode,
                    공급자명: supplier.supplierName,
                    에너지원: this.getEnergySourceName(supplier.energySourceCode),
                    상태: supplier.enabled === "Y" ? "활성" : "비활성",
                }));

                // 워크북 생성
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "공급자 목록");

                // 컬럼 너비 설정
                const columnWidths = [
                    { wch: 8 }, // 순번
                    { wch: 15 }, // 공급자코드
                    { wch: 25 }, // 공급자명
                    { wch: 12 }, // 에너지원
                    { wch: 10 }, // 상태
                ];
                worksheet["!cols"] = columnWidths;

                // 파일명 생성 (현재 날짜 포함)
                const today = new Date();
                const dateStr = today.toISOString().split("T")[0].replace(/-/g, "");
                const fileName = `공급자목록_${dateStr}.xlsx`;

                // 파일 다운로드
                XLSX.writeFile(workbook, fileName);

                this.$notify({
                    type: "success",
                    title: "성공",
                    text: "엑셀 파일이 다운로드되었습니다.",
                });
            } catch (error) {
                console.error("엑셀 다운로드 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "엑셀 파일 다운로드에 실패했습니다.",
                });
            }
        },
        openCreateForm() {
            this.isViewMode = false;
            this.isEditMode = false;
            this.editingSupplier = null;
            this.resetForm();
            this.showForm = true;
        },
        async openViewForm(supplier) {
            this.isViewMode = true;
            this.isEditMode = false;
            this.editingSupplier = supplier;

            // 상세 정보 로드
            await this.loadDetailData(supplier.supplierIdx);

            if (this.supplierDetail) {
                this.form = {
                    supplierCode: this.supplierDetail.supplierCode,
                    supplierName: this.supplierDetail.supplierName,
                    energySourceCode: this.supplierDetail.energySourceCode,
                    enabled: this.supplierDetail.enabled,
                    remarks: this.supplierDetail.remarks,
                    regDate: this.supplierDetail.regDate,
                    regWorker: this.supplierDetail.regWorker,
                    modDate: this.supplierDetail.modDate,
                    modWorker: this.supplierDetail.modWorker,
                };
            }

            this.showForm = true;
        },
        openEditForm(supplier) {
            this.isViewMode = false;
            this.isEditMode = true;
            this.editingSupplier = supplier;
            this.form = {
                supplierCode: supplier.supplierCode,
                supplierName: supplier.supplierName,
                energySourceCode: supplier.energySourceCode,
                enabled: supplier.enabled,
                remarks: supplier.remarks,
            };
            this.showForm = true;
        },
        closeForm() {
            this.showForm = false;
            this.isViewMode = false;
            this.isEditMode = false;
            this.editingSupplier = null;
            this.supplierDetail = null;
            this.resetForm();
        },
        resetForm() {
            this.form = {
                supplierCode: null,
                supplierName: null,
                energySourceCode: null,
                enabled: "Y",
                remarks: null,
            };
        },
        async handleDelete(supplier) {
            // TODO: API 연동 (다음 단계)
            const confirm = await this.alertConfirmWarning(`'${supplier.supplierName}' 공급자를 삭제하시겠습니까?`);
            if (confirm.value) {
                try {
                    const result = await backEndApi.energyBillCalc.deleteSupplier(supplier.supplierIdx);
                    if (result.status == 200) {
                        await this.alertNoti("삭제되었습니다.");
                        this.closeForm();
                        this.loadData(); // 목록 새로고침
                    } else {
                        await this.alertWarning(result.data.message);
                    }
                } catch (error) {
                    console.error("공급자 삭제 실패:", error);
                    await this.alertWarning("공급자 삭제 실패:", error);
                }
            }
        },
        async handleSave() {
            // 필수 입력 검증
            if (!this.form.supplierCode || !this.form.supplierName || !this.form.energySourceCode) {
                await this.alertWarning("필수 항목을 모두 입력해주세요.");
                return;
            }

            try {
                const payload = {
                    supplierCode: this.form.supplierCode,
                    supplierName: this.form.supplierName,
                    energySourceCode: this.form.energySourceCode,
                    enabled: this.form.enabled,
                    remarks: this.form.remarks,
                };

                let result;
                if (this.isEditMode) {
                    const confirm = await this.alertConfirmWarning("공급자 정보를 수정하시겠습니까?");
                    if (!confirm.value) return;
                    // 수정
                    payload.supplierIdx = this.editingSupplier.supplierIdx;
                    result = await backEndApi.energyBillCalc.updateSupplier(payload);
                } else {
                    const confirm = await this.alertConfirmWarning("새로운 공급자를 등록하시겠습니까?");
                    if (!confirm.value) return;
                    // 신규 등록
                    result = await backEndApi.energyBillCalc.insertSupplier(payload);
                }

                if (result.status === 200) {
                    await this.alertNoti(
                        this.isEditMode ? "공급자 정보가 수정되었습니다." : "새로운 공급자가 등록되었습니다.",
                    );
                    this.closeForm();
                    this.loadData(); // 목록 새로고침
                } else {
                    await this.alertWarning(result.data.message || "저장에 실패했습니다.");
                }
            } catch (error) {
                console.error("공급자 저장 실패:", error);
                await this.alertWarning("공급자 정보를 저장하는데 실패했습니다.");
            }
        },
    },
};
</script>

<style scoped>
.supplier-management {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* 헤더 */
.content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
}

.content-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.content-header h3 i {
    color: var(--ebc-primary);
}

.header-content-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    justify-content: flex-end;
}

.header-actions-group {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-action i {
    font-size: 12px;
}

.btn-excel {
    background: white;
    color: var(--ebc-positive);
    border-color: var(--ebc-positive);
}

.btn-excel:hover {
    background: var(--ebc-positive-tint);
}

.btn-primary {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-primary:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}

/* 헤더 안의 검색 바 */
.search-bar-header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.search-bar-header .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.search-bar-header .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.search-input-group-header {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input-group-header i {
    position: absolute;
    left: 10px;
    color: var(--ebc-text-muted);
    font-size: 12px;
    z-index: 1;
}

.search-input-header {
    padding: 6px 10px 6px 30px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    min-width: 180px;
    height: 32px;
    box-sizing: border-box;
}

.search-input-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.form-select-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 120px;
    height: 32px;
    background: var(--ebc-surface);
    cursor: pointer;
}

.form-select-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.btn-search-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search-header:hover {
    background: var(--ebc-primary-hover);
}

.btn-reset-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    color: var(--ebc-text-sub);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-reset-header:hover {
    background: var(--ebc-track);
}

/* 데이터 테이블 */
.data-table-container {
    flex: 1;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    overflow-y: auto;
    position: relative;
}

.settings-data-table {
    width: 100%;
    border-collapse: collapse;
}

.settings-data-table thead {
    background: var(--ebc-surface-sunken);
}

.settings-data-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    border-bottom: 1px solid var(--ebc-hairline);
    position: sticky;
    top: 0;
    background: var(--ebc-surface-sunken);
    z-index: 10;
}

.settings-data-table tbody tr {
    border-bottom: 1px solid var(--ebc-track);
    transition: all 0.2s;
}

.settings-data-table tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

.settings-data-table td {
    padding: 14px 16px;
    font-size: 13px;
    color: var(--ebc-text);
}

.code-text {
    font-weight: 700;
    color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
    padding: 4px 8px;
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
}

.name-text {
    font-weight: 600;
    color: var(--ebc-text);
}

.energy-badge {
    display: inline-block;
    padding: 4px 12px;
    background: var(--ebc-primary-tint);
    color: var(--ebc-primary);
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    font-weight: 600;
}

.count-badge-sm {
    display: inline-block;
    padding: 3px 8px;
    background: var(--ebc-track);
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
}

.status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    font-weight: 600;
}

.status-badge.active {
    background: var(--ebc-positive-tint);
    color: var(--ebc-positive);
}

.status-badge.inactive {
    background: var(--ebc-danger-tint);
    color: var(--ebc-danger);
}

.btn-edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-border-strong);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-text);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 4px;
}

.btn-edit:hover {
    background: var(--ebc-track);
    border-color: var(--ebc-text-muted);
}

.btn-edit i {
    font-size: 11px;
}

.btn-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-danger);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-danger);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-delete:hover {
    background: var(--ebc-danger-tint);
}

.btn-delete i {
    font-size: 11px;
}

.clickable-row {
    cursor: pointer;
}

.clickable-row:hover {
    background: var(--ebc-surface-sunken) !important;
}

.text-center {
    text-align: center !important;
}

/* 슬라이드 패널 (등록/수정 폼) */

.detail-info {
    margin-top: 24px;
    padding: 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    border: 1px solid var(--ebc-border);
}

.info-row {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px solid var(--ebc-border);
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    flex: 0 0 100px;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text-sub);
}

.info-value {
    flex: 1;
    font-size: 13px;
    color: var(--ebc-text);
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.required {
    color: var(--ebc-danger);
}

.form-input,
.form-select,
.form-textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    transition: all 0.2s;
}

.form-textarea {
    resize: none;
    overflow-y: auto;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.form-input:disabled,
.form-select:disabled,
.form-textarea:disabled {
    background: var(--ebc-track);
    color: var(--ebc-text-sub);
    cursor: not-allowed;
    border-color: var(--ebc-hairline-strong);
}

.form-input:disabled {
    background: var(--ebc-track);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
}

.help-text {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: var(--ebc-text-muted);
}

.radio-group {
    display: flex;
    gap: 12px;
    padding: 8px 0;
}

.radio-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 16px;
    border: 2px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    background: var(--ebc-surface);
    transition: all 0.3s ease;
    position: relative;
    line-height: 1;
}

.radio-label:hover:not(:has(input[type="radio"]:disabled)) {
    border-color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
}

.radio-label input[type="radio"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: var(--ebc-primary);
    margin: 0;
    vertical-align: middle;
    flex-shrink: 0;
}

.radio-label:has(input[type="radio"]:checked) {
    border-color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
    color: var(--ebc-primary);
}

/* 활성 라디오 버튼이 체크되었을 때 */
.radio-label:has(input[type="radio"][value="Y"]:checked) {
    border-color: var(--ebc-positive);
    background: var(--ebc-positive-tint);
    color: var(--ebc-positive);
}

.radio-label:has(input[type="radio"][value="Y"]:checked) input[type="radio"] {
    accent-color: var(--ebc-positive);
}

/* 비활성 라디오 버튼이 체크되었을 때 */
.radio-label:has(input[type="radio"][value="N"]:checked) {
    border-color: var(--ebc-danger);
    background: var(--ebc-danger-tint);
    color: var(--ebc-danger);
}

.radio-label:has(input[type="radio"][value="N"]:checked) input[type="radio"] {
    accent-color: var(--ebc-danger);
}

.radio-label input[type="radio"]:disabled {
    cursor: not-allowed;
}

.radio-label:has(input[type="radio"]:disabled) {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--ebc-track);
}

.radio-label span {
    user-select: none;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
}

.btn-cancel,
.btn-save {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel {
    background: white;
    color: var(--ebc-text-sub);
}

.btn-cancel:hover {
    background: var(--ebc-track);
}

.btn-save {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-save:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}
</style>
